import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Plus, Trash2, ArrowUp, ArrowDown, Upload, Image as ImageIcon } from "lucide-react";
import api, { formatErr } from "../../lib/api";
import { uploadImage } from "../../lib/uploads";

const TABS = [
  { key: "homepage_hero", label: "Hero Slider", fields: ["img", "overline", "title", "sub", "cta", "href"], imageField: "img" },
  { key: "homepage_curated", label: "Curated Selection", fields: ["brand", "subtitle", "img", "href"], imageField: "img" },
  { key: "homepage_brands", label: "Brands Marquee", fields: ["name", "logo", "href"], imageField: "logo" },
  { key: "homepage_services", label: "Watch Services", fields: ["icon", "label"], imageField: null, hasMeta: ["hero_image", "hero_alt", "cta_label", "cta_href"] },
];

const FIELD_LABELS = {
  img: "Image URL", logo: "Logo URL (optional)", overline: "Overline (small caption)", title: "Title", sub: "Subtitle / Description",
  subtitle: "Subtitle / Description", cta: "Call to Action label", href: "Link URL", brand: "Brand Name", name: "Brand Name",
  icon: "Icon (wrench / shield / award / clock / sparkles / fileCheck)", label: "Feature Label",
  hero_image: "Hero Image (right-side photo)", hero_alt: "Hero Image Alt Text", cta_label: "CTA Button Label", cta_href: "CTA Button Link",
};

export default function DevHomepage() {
  const [activeKey, setActiveKey] = useState(TABS[0].key);
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);

  const tab = TABS.find((t) => t.key === activeKey);

  const load = async (key) => {
    setLoading(true);
    try {
      const r = await api.get(`/site/content/${key}`);
      const doc = r.data || {};
      setData((prev) => ({
        ...prev,
        [key]: {
          items: Array.isArray(doc.items) ? doc.items : [],
          overline: doc.overline || "",
          title: doc.title || "",
          subtitle: doc.subtitle || "",
          hero_image: doc.hero_image || "",
          hero_alt: doc.hero_alt || "",
          cta_label: doc.cta_label || "",
          cta_href: doc.cta_href || "",
        },
      }));
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { TABS.forEach((t) => load(t.key)); /* eslint-disable-next-line */ }, []);

  const current = data[activeKey] || { items: [] };

  const updateItem = (idx, field, value) => {
    const items = [...(current.items || [])];
    items[idx] = { ...items[idx], [field]: value };
    setData((p) => ({ ...p, [activeKey]: { ...current, items } }));
  };

  const addItem = () => {
    const blank = {}; tab.fields.forEach((f) => (blank[f] = ""));
    setData((p) => ({ ...p, [activeKey]: { ...current, items: [...(current.items || []), blank] } }));
  };

  const removeItem = (idx) => {
    const items = [...(current.items || [])]; items.splice(idx, 1);
    setData((p) => ({ ...p, [activeKey]: { ...current, items } }));
  };

  const moveItem = (idx, dir) => {
    const items = [...(current.items || [])];
    const ni = idx + dir; if (ni < 0 || ni >= items.length) return;
    [items[idx], items[ni]] = [items[ni], items[idx]];
    setData((p) => ({ ...p, [activeKey]: { ...current, items } }));
  };

  const handleImageUpload = async (idx, field, file) => {
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) return toast.error("Image must be under 4 MB");
    try {
      const r = await uploadImage(file, `homepage/${activeKey}`);
      updateItem(idx, field, r.url);
      toast.success(r.storage === "s3" ? "Uploaded to S3" : "Loaded (base64 — configure S3 in Integration Keys to upload to cloud)");
    } catch (err) { toast.error(formatErr(err)); }
  };

  const save = async () => {
    try {
      await api.put(`/developer/content/${activeKey}`, current);
      toast.success(`${tab.label} saved — refresh storefront to see changes`);
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="space-y-6">
      <div><div className="overline mb-2">Storefront CMS</div><h1 className="font-display text-4xl">Homepage Editor</h1></div>

      {/* Tab strip */}
      <div className="flex gap-2 flex-wrap">
        {TABS.map((t) => {
          const selected = activeKey === t.key;
          return (
            <button
              key={t.key}
              data-testid={`hp-tab-${t.key}`}
              onClick={() => setActiveKey(t.key)}
              className={`text-[11px] uppercase tracking-luxe px-4 py-2 border transition-colors ${selected ? "border-copper" : "border-white/15 hover:border-copper/60"}`}
              style={selected ? { backgroundColor: "#B89766", color: "#fff", borderColor: "#B89766" } : undefined}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Section meta */}
      <div className="card-luxe p-5 grid md:grid-cols-3 gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">Section Overline</div>
          <input data-testid="hp-overline" className="input-box" value={current.overline || ""} onChange={(e) => setData((p) => ({ ...p, [activeKey]: { ...current, overline: e.target.value } }))} placeholder="e.g. Curated Selection"/>
        </div>
        <div className="md:col-span-2">
          <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">Section Title</div>
          <input data-testid="hp-title" className="input-box" value={current.title || ""} onChange={(e) => setData((p) => ({ ...p, [activeKey]: { ...current, title: e.target.value } }))} placeholder="e.g. Crafted Excellence — Explore Top Brands."/>
        </div>
        <div className="md:col-span-3">
          <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">Section Subtitle</div>
          <textarea className="input-box" rows={2} value={current.subtitle || ""} onChange={(e) => setData((p) => ({ ...p, [activeKey]: { ...current, subtitle: e.target.value } }))} placeholder="Optional descriptive paragraph below the title"/>
        </div>
      </div>

      {/* Services-specific meta: hero image + CTA */}
      {activeKey === "homepage_services" && (
        <div className="card-luxe p-5 grid md:grid-cols-2 gap-4" data-testid="hp-services-meta">
          <div className="md:col-span-2">
            <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">Hero Image (right side)</div>
            <div className="flex items-center gap-3 mb-2">
              {current.hero_image ? (
                <img src={current.hero_image} alt="" className="h-20 w-32 object-cover bg-white/5 border border-white/10"/>
              ) : (
                <div className="h-20 w-32 bg-white/5 border border-dashed border-white/15 flex items-center justify-center text-white/30"><ImageIcon size={16}/></div>
              )}
              <label className="cursor-pointer flex items-center gap-1.5 px-3 py-2 border border-white/15 hover:border-copper text-[11px] uppercase tracking-luxe">
                <Upload size={12}/> Upload
                <input type="file" accept="image/*" className="hidden" data-testid="hp-services-hero-upload" onChange={async (e) => {
                  const f = e.target.files?.[0]; if (!f) return;
                  if (f.size > 4 * 1024 * 1024) return toast.error("Image must be under 4 MB");
                  try {
                    const r = await uploadImage(f, "homepage/services");
                    setData((p) => ({ ...p, [activeKey]: { ...current, hero_image: r.url } }));
                    toast.success(r.storage === "s3" ? "Uploaded to S3" : "Loaded (base64)");
                  } catch (err) { toast.error(formatErr(err)); }
                }}/>
              </label>
            </div>
            <input data-testid="hp-services-hero_image" className="input-box" placeholder="Or paste URL" value={current.hero_image || ""} onChange={(e) => setData((p) => ({ ...p, [activeKey]: { ...current, hero_image: e.target.value } }))}/>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">Hero Image Alt Text</div>
            <input data-testid="hp-services-hero_alt" className="input-box" value={current.hero_alt || ""} onChange={(e) => setData((p) => ({ ...p, [activeKey]: { ...current, hero_alt: e.target.value } }))}/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">CTA Button Label</div>
              <input data-testid="hp-services-cta_label" className="input-box" value={current.cta_label || ""} onChange={(e) => setData((p) => ({ ...p, [activeKey]: { ...current, cta_label: e.target.value } }))}/>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">CTA Button Link</div>
              <input data-testid="hp-services-cta_href" className="input-box" value={current.cta_href || ""} onChange={(e) => setData((p) => ({ ...p, [activeKey]: { ...current, cta_href: e.target.value } }))}/>
            </div>
          </div>
          <div className="md:col-span-2 text-[11px] text-white/40">
            Tip: items below are the 6 feature bullets shown under the description. Icon names: wrench, shield, award, clock, sparkles, fileCheck.
          </div>
        </div>
      )}

      {/* Items */}
      <div className="space-y-4">
        {(current.items || []).map((it, idx) => (
          <div key={idx} className="card-luxe p-5" data-testid={`hp-item-${idx}`}>
            <div className="flex items-center justify-between mb-4 gap-2">
              <div className="overline">Item #{idx + 1}</div>
              <div className="flex gap-2">
                <button type="button" data-testid={`hp-up-${idx}`} onClick={() => moveItem(idx, -1)} className="p-1.5 border border-white/15 hover:border-copper" title="Move up"><ArrowUp size={12}/></button>
                <button type="button" data-testid={`hp-down-${idx}`} onClick={() => moveItem(idx, 1)} className="p-1.5 border border-white/15 hover:border-copper" title="Move down"><ArrowDown size={12}/></button>
                <button type="button" data-testid={`hp-remove-${idx}`} onClick={() => removeItem(idx)} className="p-1.5 border border-red-500/40 text-red-400 hover:bg-red-500/10" title="Remove"><Trash2 size={12}/></button>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {tab.fields.map((f) => (
                <div key={f} className={f === "sub" || f === "subtitle" ? "md:col-span-2" : ""}>
                  <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">{FIELD_LABELS[f] || f}</div>
                  {f === tab.imageField ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        {it[f] ? (
                          <img src={it[f]} alt="" className="h-16 w-24 object-cover bg-white/5 border border-white/10"/>
                        ) : (
                          <div className="h-16 w-24 bg-white/5 border border-dashed border-white/15 flex items-center justify-center text-white/30"><ImageIcon size={16}/></div>
                        )}
                        <label className="cursor-pointer flex items-center gap-1.5 px-3 py-2 border border-white/15 hover:border-copper text-[11px] uppercase tracking-luxe">
                          <Upload size={12}/> Upload
                          <input type="file" accept="image/*" className="hidden" data-testid={`hp-upload-${idx}-${f}`} onChange={(e) => handleImageUpload(idx, f, e.target.files?.[0])}/>
                        </label>
                      </div>
                      <input className="input-box" placeholder="Or paste URL" value={it[f] || ""} onChange={(e) => updateItem(idx, f, e.target.value)} data-testid={`hp-${idx}-${f}`}/>
                    </div>
                  ) : (f === "sub" || f === "subtitle") ? (
                    <textarea className="input-box" rows={2} value={it[f] || ""} onChange={(e) => updateItem(idx, f, e.target.value)} data-testid={`hp-${idx}-${f}`}/>
                  ) : (
                    <input className="input-box" value={it[f] || ""} onChange={(e) => updateItem(idx, f, e.target.value)} data-testid={`hp-${idx}-${f}`}/>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {(!current.items || current.items.length === 0) && !loading && (
          <div className="card-luxe p-10 text-center text-white/40">No items yet — click “Add Item” below to start.</div>
        )}
      </div>

      <div className="flex gap-3">
        <button type="button" data-testid="hp-add" onClick={addItem} className="btn-outline"><Plus size={13}/> Add Item</button>
        <button type="button" data-testid="hp-save" onClick={save} className="btn-primary"><Save size={13}/> Save {tab.label}</button>
      </div>
    </div>
  );
}
